import { getMatches } from './PropagateMatchesAPIs';
import { IDataCut, IFormattedMatch, IOrgJob, IPropagateMatch, ISelectedMatch, ISourceMatch, ISubmitMatchesResponse } from './PropagateMatchesTypes';

export const clearSelectedMatches = (surveyJobMatches: ISourceMatch[], matchesMade: ISubmitMatchesResponse) => {
  return surveyJobMatches.map(match => ({
    ...match,
    selectedPropagateRows: match.selectedPropagateRows.map((match: ISelectedMatch) => {
      const matchUpdated = matchesMade.successfulMatches.find((row: IPropagateMatch) => row.targetJobId === match.targetJobId && row.cutId === match.cutId);
      if (matchUpdated) return {...match, isChecked: false, matchExists: true}
      else return match;
    })
  }));
};

export const createSelectedRow = (match: IFormattedMatch, selectedRows?: ISelectedMatch[]) => {
  const existingCut = selectedRows?.find(
    selectedCut =>
      selectedCut.targetJobId === match.targetJobId &&
      match.dataCuts.some(cut => cut.cutId === selectedCut.cutId)
  );

  if (existingCut) {
    return existingCut;
  }

  const selectedCut = match.dataCuts.find(cut => cut.isDefaultAndPriority);

  if (!selectedCut) return;

  return {
    cutId: selectedCut.cutId,
    hasData: match.hasData,
    isChecked: true,
    matchExists: selectedCut.matchExists,
    surveyCode: match.surveyCode,
    targetJobId: match.targetJobId,
  };
};

export const createSourceJob = (selectedJob: IOrgJob, surveyJobMatch?: ISourceMatch) => ({
  sourceCurrency: selectedJob.currency,
  sourceJobCode: selectedJob.orgJobCode,
  sourceJobCodeKey: selectedJob.orgJobCodeKey,
  sourceJobId: selectedJob.orgJobId,
  sourceMatches: surveyJobMatch ? [surveyJobMatch] : [],
});

export const getAllAvailablePropagateRows = (surveyJobMatch: ISourceMatch, includeExisting: boolean = true) => {
  const selectedCuts = surveyJobMatch.propagateMatches
    .map(match => {
      const existingCut = match.dataCuts.find((cut: IDataCut) =>
        surveyJobMatch?.selectedPropagateRows?.some(
          (selectedCut: IDataCut) => selectedCut.cutId === cut.cutId
        )
      );

      const defaultCut = match.dataCuts.find((cut: IDataCut) => cut.isDefaultAndPriority);

      return existingCut || defaultCut;
    })
    .filter(Boolean);

  const selectedPropagateRows = surveyJobMatch.propagateMatches
    .filter(match => {
      const selectedCut = match.dataCuts.find(cut =>
        surveyJobMatch?.selectedPropagateRows?.some(
          (selectedCut: IDataCut) => selectedCut.cutId === cut.cutId
        ) || cut.isDefaultAndPriority
      );

      if (!includeExisting) {
        return match.hasData && !!selectedCut && !selectedCut?.matchExists;
      } else {
        return match.hasData && !!selectedCut;
      }
    })
    .map(match => createSelectedRow(match, surveyJobMatch.selectedPropagateRows));

  return selectedPropagateRows;
};

export const processSurveyMatch = (surveyJobMatch: ISourceMatch, surveyJobMatches: ISourceMatch[]) => {
  const currentMatch = surveyJobMatches.find(
    match => match.sourceMatchId === surveyJobMatch.sourceMatchId
  );

  const selectedPropagateRows = getAllAvailablePropagateRows(surveyJobMatch);
  const uniqueRowsMap = new Map<string, ISelectedMatch>();

  (currentMatch?.selectedPropagateRows || []).forEach((row: ISelectedMatch) => {
    const targetJobId = row?.targetJobId !== undefined ? String(row.targetJobId) : 'unknown-target-job-id';
    const surveyCode = row?.surveyCode !== undefined ? String(row.surveyCode) : 'unknown-survey-code';

    const compositeKey = `${targetJobId}-${surveyCode}`;
    uniqueRowsMap.set(compositeKey, row);
  });

  (selectedPropagateRows || []).forEach(row => {
    const targetJobId = row?.targetJobId !== undefined ? String(row.targetJobId) : 'unknown-target-job-id';
    const surveyCode = row?.surveyCode !== undefined ? String(row.surveyCode) : 'unknown-survey-code';

    const compositeKey = `${targetJobId}-${surveyCode}`;
    if (row && !uniqueRowsMap.has(compositeKey)) {
      uniqueRowsMap.set(compositeKey, row);
    }
  });

  surveyJobMatch.selectedPropagateRows = Array.from(uniqueRowsMap.values());

  return surveyJobMatch;
};
